// src/utils/backgroundLibrary.js
// Background library service for dynamic scene generation

import supabase from '../utils/supabaseUtils';

/**
 * Background library service using real Supabase data from useCESDK.js
 * Organized by sport, style, and characteristics for optimal matching
 */

// Background categories mapping from folder names to normalized sport names
export const BACKGROUND_CATEGORIES = {
  // Map folder names from your Supabase bucket to standardized sport names
  'BaseballBG': { sport: 'baseball', name: 'Baseball Fields', tags: ['field', 'stadium', 'diamond'] },
  'BasketballBG': { sport: 'basketball', name: 'Basketball Courts', tags: ['court', 'arena', 'indoor'] },
  'FootballBG': { sport: 'football', name: 'Football Fields', tags: ['field', 'stadium', 'turf'] },
  'SoftballBG': { sport: 'softball', name: 'Softball Fields', tags: ['field', 'diamond', 'outdoor'] },
  'VolleyballBG': { sport: 'volleyball', name: 'Volleyball Courts', tags: ['court', 'net', 'sand'] },
  'CheerleaderConfetti': { sport: 'cheerleading', name: 'Cheerleader', tags: ['confetti', 'spirit', 'celebration'] },
  'ColorDust': { sport: 'general', name: 'Color Dust', tags: ['abstract', 'colorful', 'dynamic'] },
  'ColorLights': { sport: 'general', name: 'Color Lights', tags: ['lights', 'colorful', 'energy'] },
  'FireSmoke': { sport: 'general', name: 'Fire & Smoke', tags: ['dramatic', 'intense', 'action'] },
  'StadiumLights': { sport: 'general', name: 'Stadium Lights', tags: ['stadium', 'lights', 'professional'] },
  'BaseballFlags': { sport: 'baseball', name: 'Baseball Flags', tags: ['patriotic', 'team', 'celebration'] }
};

/**
 * Loads backgrounds from Supabase storage using the same method as useCESDK.js
 * @returns {Promise<Array>} - Array of background assets with metadata
 */
export const loadBackgroundsFromSupabase = async () => {
  try {
    console.log("🔍 Loading backgrounds from Supabase storage...");
    
    const { data: folderData, error: folderError } = await supabase.storage
      .from('playmakerpics-images')
      .list('backgrounds', { limit: 100, offset: 0 });
    
    if (folderError) {
      console.error("❌ Error listing background folders:", folderError);
      throw folderError;
    }
    
    let allBackgrounds = [];
    
    // Only consider entries without extensions as folders
    const subfolders = folderData.filter(f => 
      !f.name.includes('.') && f.name !== '.emptyFolderPlaceholder'
    );
    
    console.log(`📁 Found ${subfolders.length} background category folders:`, subfolders.map(f => f.name));
    
    // Process each background category subfolder
    for (const folder of subfolders) {
      const folderName = folder.name;
      const categoryInfo = BACKGROUND_CATEGORIES[folderName] || {
        sport: 'general',
        name: folderName.replace(/([A-Z])/g, ' $1').replace(/^./, match => match.toUpperCase()),
        tags: ['general']
      };
      
      console.log(`🏷️ Processing folder "${folderName}" as sport "${categoryInfo.sport}"`);
      
      const subPath = `backgrounds/${folderName}`;
      const { data: fileData, error: fileError } = await supabase.storage
        .from('playmakerpics-images')
        .list(subPath, { limit: 100, offset: 0 });
      
      if (fileError) {
        console.error(`❌ Error listing files in ${subPath}:`, fileError);
        continue;
      }
      
      // Process image files in this folder
      const folderBackgrounds = fileData
        .filter(file => /\.(jpg|jpeg|png|gif)$/i.test(file.name))
        .map(file => {
          const encodedFileName = encodeURIComponent(file.name);
          const fullPath = `${subPath}/${encodedFileName}`;
          const { data: urlData } = supabase.storage
            .from('playmakerpics-images')
            .getPublicUrl(fullPath);
            
          if (!urlData || !urlData.publicUrl) {
            console.warn(`⚠️ Could not get public URL for ${fullPath}`);
            return null;
          }
            
          const uri = `${urlData.publicUrl}?t=${Date.now()}`;
          
          // Extract additional tags from filename
          const filename = file.name.toLowerCase();
          const additionalTags = [];
          
          // Add tags based on filename keywords
          if (filename.includes('stadium')) additionalTags.push('stadium');
          if (filename.includes('field')) additionalTags.push('field');
          if (filename.includes('court')) additionalTags.push('court');
          if (filename.includes('night')) additionalTags.push('night');
          if (filename.includes('day')) additionalTags.push('day');
          if (filename.includes('sunset')) additionalTags.push('sunset');
          if (filename.includes('lights')) additionalTags.push('lights');
          if (filename.includes('grass')) additionalTags.push('grass');
          if (filename.includes('indoor')) additionalTags.push('indoor');
          if (filename.includes('outdoor')) additionalTags.push('outdoor');
          
          // Determine mood from filename and folder
          let mood = 'energetic';
          if (filename.includes('sunset') || filename.includes('golden')) mood = 'inspiring';
          if (filename.includes('dark') || filename.includes('night')) mood = 'dramatic';
          if (filename.includes('bright') || filename.includes('sunny')) mood = 'optimistic';
          if (categoryInfo.sport === 'cheerleading') mood = 'celebratory';
          if (folderName.includes('Fire') || folderName.includes('Smoke')) mood = 'intense';
          
          return {
            sport: categoryInfo.sport,
            subcategory: determineSubcategory(folderName, filename),
            url: uri,
            tags: [...categoryInfo.tags, ...additionalTags],
            mood: mood,
            filename: file.name,
            folder: folderName,
            category: categoryInfo.name
          };
        })
        .filter(bg => bg !== null);
      
      console.log(`🖼️ Processed ${folderBackgrounds.length} backgrounds from ${folderName}`);
      allBackgrounds = allBackgrounds.concat(folderBackgrounds);
    }
    
    // Also check for images directly in the backgrounds folder (uncategorized)
    const rootImages = folderData.filter(f => /\.(jpg|jpeg|png|gif)$/i.test(f.name));
    if (rootImages.length > 0) {
      console.log(`📷 Found ${rootImages.length} uncategorized backgrounds`);
      
      const uncategorizedBackgrounds = rootImages.map(file => {
        const encodedFileName = encodeURIComponent(file.name);
        const path = `backgrounds/${encodedFileName}`;
        const { data: urlData } = supabase.storage
          .from('playmakerpics-images')
          .getPublicUrl(path);
          
        if (!urlData || !urlData.publicUrl) {
          console.warn(`⚠️ Could not get public URL for ${path}`);
          return null;
        }
          
        const uri = `${urlData.publicUrl}?t=${Date.now()}`;
        
        return {
          sport: 'general',
          subcategory: 'general',
          url: uri,
          tags: ['general', 'versatile'],
          mood: 'neutral',
          filename: file.name,
          folder: 'root',
          category: 'General'
        };
      }).filter(bg => bg !== null);
      
      allBackgrounds = allBackgrounds.concat(uncategorizedBackgrounds);
    }
    
    console.log(`✅ Loaded ${allBackgrounds.length} total backgrounds from Supabase`);
    
    // Group by sport for easy access
    const backgroundsBySport = allBackgrounds.reduce((acc, bg) => {
      if (!acc[bg.sport]) acc[bg.sport] = {};
      if (!acc[bg.sport][bg.subcategory]) acc[bg.sport][bg.subcategory] = [];
      acc[bg.sport][bg.subcategory].push(bg);
      return acc;
    }, {});
    
    return { allBackgrounds, backgroundsBySport };
    
  } catch (error) {
    console.error("❌ Error loading backgrounds from Supabase:", error);
    return { allBackgrounds: [], backgroundsBySport: {} };
  }
};

/**
 * Determines subcategory based on folder name and filename
 */
const determineSubcategory = (folderName, filename) => {
  const folder = folderName.toLowerCase();
  const file = filename.toLowerCase();
  
  // Field/Court backgrounds
  if (folder.includes('field') || folder.includes('bg') || file.includes('field') || file.includes('court')) {
    return 'field';
  }
  
  // Stadium backgrounds
  if (file.includes('stadium') || file.includes('arena') || folder.includes('stadium')) {
    return 'stadium';
  }
  
  // Action/dynamic backgrounds
  if (folder.includes('lights') || folder.includes('fire') || folder.includes('smoke') || folder.includes('dust')) {
    return 'action';
  }
  
  // Team/celebration backgrounds
  if (folder.includes('confetti') || folder.includes('flags') || folder.includes('cheerleader')) {
    return 'team';
  }
  
  return 'field'; // Default subcategory
};

/**
 * Analyzes content and event context to select optimal backgrounds
 * Now uses real Supabase data instead of mock data
 */
export const selectOptimalBackgrounds = async (contentAnalysis, eventTags = {}, options = {}) => {
  console.log('🎯 Selecting optimal backgrounds from Supabase storage');
  
  try {
    // Load backgrounds from Supabase
    const { allBackgrounds, backgroundsBySport } = await loadBackgroundsFromSupabase();
    
    if (allBackgrounds.length === 0) {
      console.warn('No backgrounds loaded from Supabase');
      return [];
    }
    
    // Determine sport from analysis and event tags
    const sport = determineTargetSport(contentAnalysis, eventTags);
    
    // Get subcategory based on content characteristics
    const subcategory = determineBackgroundSubcategory(contentAnalysis, eventTags);
    
    // Filter backgrounds by sport and subcategory
    const candidates = getBackgroundCandidates(sport, subcategory, backgroundsBySport);
    
    // Score and rank backgrounds based on content analysis
    const scoredBackgrounds = scoreBackgrounds(candidates, contentAnalysis, eventTags);
    
    // Return top recommendations
    const topRecommendations = scoredBackgrounds.slice(0, options.maxResults || 5);
    
    console.log(`✅ Selected ${topRecommendations.length} optimal backgrounds for ${sport}/${subcategory}`);
    
    return topRecommendations;
    
  } catch (error) {
    console.error('Error selecting optimal backgrounds:', error);
    // Return empty array on error
    return [];
  }
};

/**
 * Determines target sport from analysis and event context
 */
const determineTargetSport = (contentAnalysis, eventTags) => {
  // First priority: Event tags
  if (eventTags.sport) {
    return eventTags.sport.toLowerCase();
  }
  
  // Second priority: AI analysis
  if (contentAnalysis?.sport?.primary) {
    return contentAnalysis.sport.primary.toLowerCase();
  }
  
  // Third priority: Event type detection
  if (eventTags.eventType) {
    const eventType = eventTags.eventType.toLowerCase();
    if (eventType.includes('baseball')) return 'baseball';
    if (eventType.includes('basketball')) return 'basketball';
    if (eventType.includes('football')) return 'football';
    if (eventType.includes('soccer')) return 'soccer';
    if (eventType.includes('softball')) return 'softball';
    if (eventType.includes('volleyball')) return 'volleyball';
  }
  
  // Default fallback
  return 'baseball';
};

/**
 * Determines background subcategory based on content characteristics
 */
const determineBackgroundSubcategory = (contentAnalysis, eventTags) => {
  const characteristics = contentAnalysis?.characteristics || {};
  const layout = contentAnalysis?.suggestedLayout || '';
  
  // Team photos → Team/celebration backgrounds
  if (characteristics.peopleCount > 1 || layout.includes('team')) {
    return 'team';
  }
  
  // Action shots → Action backgrounds
  if (layout.includes('action') || characteristics.isLandscape) {
    return 'action';
  }
  
  // Portrait photos → Field/court backgrounds (less distracting)
  if (characteristics.isPortrait || layout.includes('portrait')) {
    return 'field';
  }
  
  // Stadium/professional context
  if (eventTags.level === 'professional' || eventTags.venue === 'stadium') {
    return 'stadium';
  }
  
  // Default to field/court
  return 'field';
};

/**
 * Gets background candidates from the loaded backgrounds
 */
const getBackgroundCandidates = (sport, subcategory, backgroundsBySport) => {
  // Try exact sport match first
  let candidates = backgroundsBySport[sport]?.[subcategory] || [];
  
  // If no exact match, try same sport different subcategory
  if (candidates.length === 0 && backgroundsBySport[sport]) {
    candidates = Object.values(backgroundsBySport[sport]).flat();
  }
  
  // If still no match, try general/similar sports
  if (candidates.length === 0) {
    // For baseball, also include softball
    if (sport === 'baseball' && backgroundsBySport.softball) {
      candidates = Object.values(backgroundsBySport.softball).flat();
    }
    // For softball, also include baseball
    else if (sport === 'softball' && backgroundsBySport.baseball) {
      candidates = Object.values(backgroundsBySport.baseball).flat();
    }
    // Fallback to general backgrounds
    else if (backgroundsBySport.general) {
      candidates = Object.values(backgroundsBySport.general).flat();
    }
  }
  
  return candidates || [];
};

/**
 * Scores backgrounds based on how well they match content and context
 */
const scoreBackgrounds = (candidates, contentAnalysis, eventTags) => {
  return candidates.map(background => {
    let score = 0.5; // Base score
    
    // Mood matching
    const mood = background.mood || 'neutral';
    if (eventTags.mood && mood === eventTags.mood) {
      score += 0.3;
    }
    
    // Tag matching
    const bgTags = background.tags || [];
    const eventTagList = Object.values(eventTags)
      .flat()
      .filter(tag => tag !== null && tag !== undefined); // Filter out null/undefined values
    const tagMatches = bgTags.filter(tag => 
      eventTagList.some(eventTag => 
        eventTag && eventTag.toString().toLowerCase().includes(tag.toLowerCase())
      )
    ).length;
    score += (tagMatches * 0.1);
    
    // Brightness matching
    const contentBrightness = contentAnalysis?.characteristics?.brightness || 'medium';
    if (bgTags.includes(contentBrightness) || 
        (contentBrightness === 'bright' && bgTags.includes('sunny')) ||
        (contentBrightness === 'dark' && bgTags.includes('dramatic'))) {
      score += 0.2;
    }
    
    // Time of day matching
    if (eventTags.timeOfDay) {
      const timeTag = eventTags.timeOfDay.toLowerCase();
      if (bgTags.includes(timeTag) || 
          (timeTag === 'evening' && bgTags.includes('sunset')) ||
          (timeTag === 'night' && bgTags.includes('lights'))) {
        score += 0.15;
      }
    }
    
    // Competitive level matching
    if (eventTags.level) {
      const level = eventTags.level.toLowerCase();
      if ((level.includes('professional') && bgTags.includes('stadium')) ||
          (level.includes('youth') && bgTags.includes('field')) ||
          (level.includes('high') && bgTags.includes('stadium'))) {
        score += 0.15;
      }
    }
    
    return {
      ...background,
      matchScore: Math.min(1.0, score),
      matchReasons: generateMatchReasons(background, contentAnalysis, eventTags, score)
    };
  }).sort((a, b) => b.matchScore - a.matchScore);
};

/**
 * Generates human-readable reasons for background selection
 */
const generateMatchReasons = (background, contentAnalysis, eventTags, score) => {
  const reasons = [];
  
  if (score > 0.8) {
    reasons.push('Perfect match for your content and event context');
  } else if (score > 0.6) {
    reasons.push('Great fit based on sport and photo characteristics');
  }
  
  if (background.mood && eventTags.mood === background.mood) {
    reasons.push(`Matches ${background.mood} mood perfectly`);
  }
  
  if (background.sport === eventTags.sport) {
    reasons.push(`Optimized for ${background.sport} sports`);
  }
  
  if (background.tags?.includes('stadium') && eventTags.level === 'professional') {
    reasons.push('Professional stadium background');
  }
  
  return reasons.length > 0 ? reasons : [`Versatile ${background.category} background`];
};

export default {
  selectOptimalBackgrounds,
  loadBackgroundsFromSupabase,
  BACKGROUND_CATEGORIES
}; 